import { SerializedConstructor } from '@langchain/core/load/serializable';
import { TemplateFormat } from '@langchain/core/prompts';
import { InfoCircleIcon } from '@langchain/untitled-ui-icons';

import { FC } from 'react';

import { ButtonSwitchSimple } from '@/components/ButtonSwitch';
import PromptTemplateFormatDropdown from '@/components/PromptTemplateFormatDropdown';
import { Tooltip } from '@/design-system';
import { EPromptType } from '@/types/schema';
import { useLocalStorageState } from '@/utils/use-local-storage-state.tsx';

import {
  changeTemplateFormat,
  createPlaygroundManifest,
  getDefaultPromptManifest,
  promptTypeFromManifest,
} from '../../utils/Playground.utils';
import { extractTemplateFormatFromKwargs } from '../manifest/serialized/prompts/ManifestChatPromptTemplate.utils';
import SettingsModalSection from './SettingsModalSection';

export const PREFERRED_PROMPT_TEMPLATE_FORMAT_STORAGE_KEY =
  'ls:preferred-prompt-template-format';

type PromptFormatProps = {
  manifest: SerializedConstructor;
  setManifest: (manifest: SerializedConstructor) => void;
  setEdited: (value: boolean) => void;
  onSettingsChange?: () => void;
};

const PromptFormatSection: FC<PromptFormatProps> = ({
  manifest,
  setManifest,
  setEdited,
  onSettingsChange,
}) => {
  const promptType = promptTypeFromManifest(manifest);
  const templateFormat = extractTemplateFormatFromKwargs(
    manifest.kwargs.first.kwargs
  );
  const [_, setPreferredPromptFormat] = useLocalStorageState<TemplateFormat>(
    PREFERRED_PROMPT_TEMPLATE_FORMAT_STORAGE_KEY,
    templateFormat
  );
  return (
    <SettingsModalSection
      title="Prompt Format"
      subtitle="Specify prompt type and variable formatting."
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-center gap-2">
            <span className="text-sm font-medium">Prompt type</span>
            <Tooltip
              title="Chat models support multi-turn conversations. Completion models generate continuations of a given text"
              side="top"
            >
              <div>
                <InfoCircleIcon className="my-auto h-4 w-4" />
              </div>
            </Tooltip>
          </div>
          <div className="items-left flex gap-2">
            <ButtonSwitchSimple
              value={promptType}
              onChange={(type: EPromptType.CHAT | EPromptType.INSTRUCT) => {
                setEdited(true);
                onSettingsChange?.();
                const modelManifest = manifest.kwargs.last;
                setManifest(
                  createPlaygroundManifest(
                    getDefaultPromptManifest({
                      modelManifest,
                      type,
                      templateFormat,
                    }),
                    modelManifest
                  )
                );
              }}
              options={[
                { label: 'Chat', value: EPromptType.CHAT },
                { label: 'Instruct', value: EPromptType.INSTRUCT },
              ]}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">Template format</span>
          <div className="ml-1 mr-auto mt-2 w-fit">
            <PromptTemplateFormatDropdown
              templateFormat={templateFormat}
              onTemplateFormatSwitch={(newFormat) => {
                setEdited(true);
                onSettingsChange?.();
                setPreferredPromptFormat(newFormat);
                setManifest(changeTemplateFormat(manifest, newFormat));
              }}
            />
          </div>
        </div>
      </div>
    </SettingsModalSection>
  );
};

export default PromptFormatSection;
